import { useState, useRef } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Does a dropped file satisfy an `accept` string like "image/*,video/*"? */
function matchesAccept(file: File, accept: string): boolean {
  return accept
    .split(",")
    .map((entry) => entry.trim())
    .some((entry) => {
      if (entry === "*/*" || entry === "") return true;
      if (entry.endsWith("/*")) {
        return file.type.startsWith(entry.slice(0, -1));
      }
      if (entry.startsWith(".")) {
        return file.name.toLowerCase().endsWith(entry.toLowerCase());
      }
      return file.type === entry;
    });
}

interface ImageUploadProps {
  imageUrl?: string | null;
  onUpload: (file: File) => void;
  onRemove?: () => void;
  isUploading?: boolean;
  className?: string;
  /** File types to accept. Defaults to images only. */
  accept?: string;
  /** Allow picking or dropping several files at once. */
  multiple?: boolean;
  /** Receives every accepted file when `multiple` is set. */
  onUploadFiles?: (files: File[]) => void;
  /** Hint shown under the drop zone. */
  hint?: string;
}

export function ImageUpload({
  imageUrl,
  onUpload,
  onRemove,
  isUploading,
  className,
  accept = "image/*",
  multiple = false,
  onUploadFiles,
  hint = "PNG, JPG up to 10MB",
}: ImageUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files ?? []).filter((file) =>
      matchesAccept(file, accept),
    );
    if (files.length === 0) return;

    if (multiple && onUploadFiles) onUploadFiles(files);
    else onUpload(files[0]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    if (multiple && onUploadFiles) onUploadFiles(files);
    else onUpload(files[0]);
    // Allow re-selecting the same file after a failed upload.
    e.target.value = "";
  };

  if (imageUrl) {
    return (
      <div
        className={cn("relative rounded-lg overflow-hidden border", className)}
      >
        <img
          src={imageUrl}
          alt="Floor plan"
          className="w-full h-full object-contain bg-muted"
        />
        {onRemove && (
          <Button
            offlineEdit
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-8 w-8"
            onClick={onRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors",
        dragActive
          ? "border-primary bg-primary/5"
          : "border-muted-foreground/25",
        className,
      )}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {isUploading ? (
        <>
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Uploading...</p>
        </>
      ) : (
        <>
          <ImageIcon className="h-8 w-8 text-muted-foreground" />
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Drag and drop an image here, or
            </p>
            <Button
              offlineEdit
              variant="link"
              size="sm"
              className="p-0 h-auto"
              onClick={() => inputRef.current?.click()}
            >
              browse files
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  );
}
